import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faCalculatorSimple } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, TableBody, TableHead, TableRow, Typography } from '@mui/material';

import { calculationQueries } from '@repo/api/claim/calculations';
import { isCalculatedLoss } from '@repo/api/claim/calculations.types';
import { claimQueries } from '@repo/api/claim/claims';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import { timeNames } from '@repo/api/common.types';
import useCurrency from '@repo/features/hooks/useCurrency';

import { CalculationTable } from '~pages/_claims/claim.$claimId/_claim/calculations/-components/CalculationTable';
import { Cell } from './-components/Cell';
import { CurrencyCell } from './-components/CurrencyCell';

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/loss',
)({
	component: function LossCalculationsPage() {
		const { claimId } = Route.useParams();

		const [{ data: claim }, { data }, { data: questionnaire }] =
			useSuspenseQueries({
				queries: [
					claimQueries.id(claimId),
					calculationQueries.loss(claimId),
					questionnaireQueries.id(claimId),
				],
			});

		const currency = useCurrency(claim.claimInsured.countryId);
		const isTierTwo = claim.isTier2Enabled && questionnaire.isTier2;

		if (!isCalculatedLoss(data)) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						p: 4,
						gap: 1,
						border: '1px solid',
						borderColor: 'accent.main',
						width: '600px',
						maxWidth: '100%',
						mx: 'auto',
					}}
				>
					<FontAwesomeIcon icon={faCalculatorSimple} size="2xl" />
					<Typography variant="subtitle1">No Calculations Data</Typography>
				</Box>
			);
		}

		const {
			dailySalesFigure,
			indemnityPeriod,
			indemnityPeriodType,
			salesLoss,
			savedCosts,
			netBiLoss,
			collectibleRate,
			biLoss,
			biLimit,
			biLossPayout,
			averageAdjustment,
			limitOfSumInsured,
			biLossConsideringAverageAdjustment,
		} = data;

		return (
			<Box
				sx={{
					width: '420px',
					maxWidth: '100%',
				}}
			>
				<CalculationTable>
					<TableHead>
						<TableRow>
							<Cell header>Description</Cell>
							<Cell header sx={{ width: 0 }}>
								Amount
							</Cell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{!isTierTwo && (
							<TableRow className="striped">
								<Cell>Projected Sales - Daily Average</Cell>
								<CurrencyCell {...currency}>{dailySalesFigure}</CurrencyCell>
							</TableRow>
						)}
						<TableRow className="striped">
							<Cell>
								Indemnity (Restoration) Period -{' '}
								{timeNames[indemnityPeriodType]}
							</Cell>
							<Cell number underline={isTierTwo ? 'double' : undefined}>
								{indemnityPeriod}
							</Cell>
						</TableRow>
						<TableRow className="striped">
							<Cell>
								Waiting Period
								{claim.claimPolicy?.waitingPeriod ?
									claim.claimPolicy.waitingPeriodDurationHours ?
										' - Hours'
									:	' - Days'
								:	''}
							</Cell>
							<Cell
								number
								align="right"
								underline={isTierTwo ? 'double' : true}
							>
								{claim.claimPolicy?.waitingPeriodDuration ?? 0}
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Sales Loss</Cell>
							<CurrencyCell {...currency}>{salesLoss}</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Less: Saved Costs</Cell>
							<CurrencyCell {...currency} hideSymbol underline>
								{savedCosts}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>BI Loss</Cell>
							<CurrencyCell {...currency} underline="double">
								{netBiLoss}
							</CurrencyCell>
						</TableRow>
						{averageAdjustment != null && averageAdjustment > 0 && (
							<>
								<TableRow>
									<Cell spacer />
								</TableRow>
								<TableRow className="striped">
									<Cell>Average Adjustment</Cell>
									<Cell percent>{averageAdjustment}</Cell>
								</TableRow>
							</>
						)}
						{biLossConsideringAverageAdjustment != null &&
							biLossConsideringAverageAdjustment > 0 && (
								<>
									<TableRow>
										<Cell spacer />
									</TableRow>
									<TableRow className="striped">
										<Cell>BI Loss Considering Average Adjustment</Cell>
										<CurrencyCell {...currency} underline="double">
											{biLossConsideringAverageAdjustment}
										</CurrencyCell>
									</TableRow>
								</>
							)}
						{limitOfSumInsured != null &&
							limitOfSumInsured > 0 &&
							((biLossConsideringAverageAdjustment &&
								limitOfSumInsured < biLossConsideringAverageAdjustment) ||
								(!biLossConsideringAverageAdjustment &&
									limitOfSumInsured < netBiLoss)) && (
								<>
									<TableRow>
										<Cell spacer />
									</TableRow>
									<TableRow className="striped">
										<Cell>Limit of Sum Insured</Cell>
										<CurrencyCell {...currency} underline>
											{limitOfSumInsured}
										</CurrencyCell>
									</TableRow>
								</>
							)}
						{collectibleRate != null && collectibleRate > 0 && biLoss != null ?
							<>
								<TableRow>
									<Cell spacer />
								</TableRow>
								<TableRow className="striped">
									<Cell>Collectible Rate</Cell>
									<Cell percent>{collectibleRate}</Cell>
								</TableRow>
								<TableRow className="striped">
									<Cell>Collectible Loss</Cell>
									<CurrencyCell {...currency} underline="double">
										{biLoss}
									</CurrencyCell>
								</TableRow>
								{biLimit != null && biLimit > 0 && biLoss > biLimit && (
									<TableRow className="striped">
										<Cell>BI Limit</Cell>
										<CurrencyCell {...currency} underline="double">
											{biLimit}
										</CurrencyCell>
									</TableRow>
								)}
							</>
						: biLimit != null && biLimit > 0 && netBiLoss > biLimit ?
							<TableRow className="striped">
								<Cell>Lesser of BI Loss or BI Limit</Cell>
								<CurrencyCell {...currency} underline="double">
									{biLossPayout}
								</CurrencyCell>
							</TableRow>
						:	null}
					</TableBody>
				</CalculationTable>
			</Box>
		);
	},
});
